import { RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Invoice, InvoiceLineItem, SKU, ValidationMismatchEntry } from 'src/redux/billing/billing.types';
import InvoiceLineItemNoteModal from './invoice-line-item-note-modal';
import { Company } from '../../types/billing.types';
import InvoiceDrawerLineTableRow from './invoice-drawer-line-table-row';

interface InvoiceDrawerLineTableProps {
  isLocked: boolean;
  invoice: Invoice | null | undefined;
  skus?: Array<SKU>;
  invoices: Invoice[];
  companies: Company[];
  updateLineItem: (lineItemId: string, field: string, value: any) => Promise<void>;
  refetchInvoiceGroup: () => void | Promise<void>;
  validationMismatches?: ValidationMismatchEntry[];
}

const InvoiceDrawerLineTable: React.FC<InvoiceDrawerLineTableProps> = ({
  isLocked,
  invoice,
  skus = [],
  invoices,
  companies,
  updateLineItem,
  refetchInvoiceGroup,
  validationMismatches,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState<InvoiceLineItem | null>(null);

  const handleUpdateLineItem = async(lineItemId: string, field: string, value: any) => {
    setIsLoading(true);

    await updateLineItem(lineItemId, field, value);
    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedLineItem && invoice) {
      const updatedLineItem = invoice?.invoice_line_items.find((lineItem) => lineItem.id === selectedLineItem?.id);
      if (updatedLineItem) {
        setSelectedLineItem(updatedLineItem);
      }
    }
  }, [selectedLineItem, invoice]);

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute top-2 left-2 flex justify-left items-center z-50 p-3 bg-blue-500/80 text-white rounded-full w-fit">
          <RefreshCw className="h-5 w-5 text-white animate-bounce animate-spin" />
        </div>
      )}
      <Table className={`w-full ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>

        <TableHeader>
          <TableRow>
            <TableHead className='text-gray-600 text-sm font-bold w-[50px] min-w-[50px] max-w-[50px]' />
            <TableHead className='text-gray-600 text-sm font-bold w-[150px] min-w-[150px] max-w-[150px]'>
              SKU/Product
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[200px] min-w-[200px] max-w-[200px]'>
              Description
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[100px] min-w-[100px] max-w-[100px]'>
              Quantity
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[100px] min-w-[100px] max-w-[100px]'>
              Rate
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[100px] min-w-[100px] max-w-[100px]'>
              Margin %
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[100px] min-w-[100px] max-w-[100px]'>
              Spend
            </TableHead>
            <TableHead className='text-gray-600 text-sm font-bold w-[20px] min-w-[20px] max-w-[20px]' />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoice?.invoice_line_items.filter((lineItem) => !lineItem.hide).sort((a, b) => a.name.localeCompare(b.name)).map((lineItem) => (
            <InvoiceDrawerLineTableRow
              key={lineItem.id}
              lineItem={lineItem}
              invoice={invoice}
              skus={skus}
              invoices={invoices}
              companies={companies}
              isLocked={isLocked}
              updateLineItem={updateLineItem}
              refetchInvoiceGroup={refetchInvoiceGroup}
              validationMismatches={validationMismatches}
            />
          ))}
        </TableBody>
      </Table>
      <InvoiceLineItemNoteModal
        invoiceLineItem={selectedLineItem}
        isOpen={isNoteModalOpen}
        onOpenChange={setIsNoteModalOpen}
        updateLineItem={handleUpdateLineItem}
      />
    </div>
  );
};

export default InvoiceDrawerLineTable;
